import type { MyCareerCenterInfoQuery } from 'core/graphql';

/** `status` is a plain String on the wire (spec 0018 §4). */
export type CareerMemberStatus = 'ACTIVE' | 'PAUSED' | 'PLACED' | 'ARCHIVED';

export interface ICareerCenter {
  memberId: string;
  status: CareerMemberStatus;
  /** Null when the member has no direction assigned yet. */
  directionTitle: string | null;
  enrolledAt: string;
  lessonsCompleted: number;
  lessonsTotal: number;
  /** Overall progress, 0–100. 0 when the track has no published lessons yet. */
  progressPercent: number;
}

const KNOWN_STATUSES: CareerMemberStatus[] = ['ACTIVE', 'PAUSED', 'PLACED', 'ARCHIVED'];

const isKnownStatus = (value: string): value is CareerMemberStatus =>
  (KNOWN_STATUSES as string[]).includes(value);

/**
 * Normalizes `myCareerCenter` into a view model.
 *
 * `null` in / `null` out is load-bearing: a missing membership is how the
 * backend says "this student is not in the Career Center" (spec 0018 §3.1) —
 * it is NOT an error, and every gate in the section (menu entry, route,
 * lesson list) reads exactly this null.
 *
 * An unknown `status` from a newer backend degrades to 'ACTIVE' rather than
 * crashing — the student keeps access; membership itself is the real gate.
 */
export const transformCareerCenter = (
  data: MyCareerCenterInfoQuery | undefined,
): ICareerCenter | null => {
  const center = data?.myCareerCenter;

  if (!center) {
    return null;
  }

  return {
    memberId: center.memberId,
    status: isKnownStatus(center.status) ? center.status : 'ACTIVE',
    directionTitle: center.directionTitle ?? null,
    enrolledAt: center.enrolledAt,
    lessonsCompleted: center.lessonsCompleted,
    lessonsTotal: center.lessonsTotal,
    progressPercent: toProgressPercent(center.lessonsCompleted, center.lessonsTotal),
  };
};

/** Guards against a 0-lesson track (division by zero) and clamps to 0–100. */
export const toProgressPercent = (completed: number, total: number): number => {
  if (!total || total <= 0) {
    return 0;
  }
  return Math.min(100, Math.max(0, Math.round((completed / total) * 100)));
};
